import React, { useState, useEffect } from "react";
import { Gear, FloppyDisk, Warning, CheckCircle } from "@phosphor-icons/react";
import { api } from "../../utils/api";
import { comprimirImagen } from "../../utils/imagenes";
import { Button } from "../shared/Button";
import { Input, Textarea } from "../shared/Input";
import { Loading } from "../shared/EmptyState";

export const Configuracion = ({ adminToken }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [empresaPago, setEmpresaPago] = useState({ iban: "", bizum: "", titular_cuenta: "" });
  const [guardandoPago, setGuardandoPago] = useState(false);
  const [empresaDatos, setEmpresaDatos] = useState({ nombre: "", cif: "", telefono: "", email: "", direccion: "" });
  const [guardandoDatos, setGuardandoDatos] = useState(false);
  const [empresaMarca, setEmpresaMarca] = useState({ logo_url: "", slogan: "", horario: "", garantia_texto: "", web: "" });
  const [guardandoMarca, setGuardandoMarca] = useState(false);
  const [empresaTarifa, setEmpresaTarifa] = useState({ tarifa_hora_peon: 18 });
  const [guardandoTarifa, setGuardandoTarifa] = useState(false);

  useEffect(() => {
    cargarConfiguracion();
    cargarDatosPago();
    cargarDatosEmpresa();
    cargarDatosMarca();
    cargarDatosTarifa();
  }, []);

  const cargarConfiguracion = async () => {
    try {
      const data = await api.get("/admin/configuracion", adminToken);
      setConfig(data);
    } catch (e) {
      console.error("Error:", e);
    } finally {
      setLoading(false);
    }
  };

  const cargarDatosPago = async () => {
    try {
      const data = await api.get("/empresa");
      setEmpresaPago({
        iban: data?.iban || "",
        bizum: data?.bizum || "",
        titular_cuenta: data?.titular_cuenta || "",
      });
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const guardarDatosPago = async () => {
    setGuardandoPago(true);
    try {
      await api.put("/admin/empresa", empresaPago, adminToken);
      setMensaje({ tipo: "success", texto: "✅ Datos de cobro guardados" });
    } catch (e) {
      setMensaje({ tipo: "error", texto: "❌ Error al guardar: " + e.message });
    } finally {
      setGuardandoPago(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  const cargarDatosEmpresa = async () => {
    try {
      const data = await api.get("/empresa");
      setEmpresaDatos({
        nombre: data?.nombre || "",
        cif: data?.cif || "",
        telefono: data?.telefono || "",
        email: data?.email || "",
        direccion: data?.direccion || "",
      });
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const guardarDatosEmpresa = async () => {
    setGuardandoDatos(true);
    try {
      await api.put("/admin/empresa", empresaDatos, adminToken);
      setMensaje({ tipo: "success", texto: "✅ Datos de la empresa guardados" });
    } catch (e) {
      setMensaje({ tipo: "error", texto: "❌ Error al guardar: " + e.message });
    } finally {
      setGuardandoDatos(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  const cargarDatosMarca = async () => {
    try {
      const data = await api.get("/empresa");
      setEmpresaMarca({
        logo_url: data?.logo_url || "",
        slogan: data?.slogan || "",
        horario: data?.horario || "",
        garantia_texto: data?.garantia_texto || "",
        web: data?.web || "",
      });
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const cargarDatosTarifa = async () => {
    try {
      const data = await api.get("/empresa");
      setEmpresaTarifa({
        tarifa_hora_peon: data?.tarifa_hora_peon ?? 18,
      });
    } catch (e) {
      console.error("Error:", e);
    }
  };

  const guardarDatosTarifa = async () => {
    setGuardandoTarifa(true);
    try {
      await api.put("/admin/empresa", empresaTarifa, adminToken);
      setMensaje({ tipo: "success", texto: "✅ Tarifa de mano de obra guardada" });
    } catch (e) {
      setMensaje({ tipo: "error", texto: "❌ Error al guardar: " + e.message });
    } finally {
      setGuardandoTarifa(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // PNG (no JPEG): el logo suele tener fondo transparente, y JPEG le
      // pintaría un fondo negro por debajo sin avisar. maxAncho más chico
      // porque un logo no necesita 1600px — nunca se muestra tan grande.
      const comprimido = await comprimirImagen(file, { maxAncho: 600, formato: "image/png" });
      setEmpresaMarca((p) => ({ ...p, logo_url: comprimido }));
    } catch (err) {
      console.error("Error comprimiendo logo:", err);
      alert("No se pudo procesar la imagen — probá con otra.");
    }
  };

  const guardarDatosMarca = async () => {
    setGuardandoMarca(true);
    try {
      await api.put("/admin/empresa", empresaMarca, adminToken);
      setMensaje({ tipo: "success", texto: "✅ Marca y presentación guardadas" });
    } catch (e) {
      setMensaje({ tipo: "error", texto: "❌ Error al guardar: " + e.message });
    } finally {
      setGuardandoMarca(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  const guardarConfiguracion = async () => {
    setGuardando(true);
    setMensaje(null);
    try {
      await api.put("/admin/configuracion", config, adminToken);
      setMensaje({ tipo: "success", texto: "✅ Configuración guardada correctamente" });
    } catch (e) {
      setMensaje({ tipo: "error", texto: "❌ Error al guardar: " + e.message });
    } finally {
      setGuardando(false);
      setTimeout(() => setMensaje(null), 5000);
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="pb-24">
      <div className="container pt-4">
        <div className="mb-6">
          <h1 className="text-h2 font-display mb-1">⚙️ Configuración</h1>
          <p className="text-small text-secondary">Gestiona la configuración del sistema</p>
        </div>

        {mensaje && (
          <div
            className="card p-4 mb-4"
            style={{
              background: mensaje.tipo === "success" ? "var(--success-bg)" : "var(--danger-bg)",
              border: `1px solid ${mensaje.tipo === "success" ? "var(--success)" : "var(--danger)"}`
            }}
          >
            <div className="flex items-center gap-2">
              {mensaje.tipo === "success" ? (
                <CheckCircle size={20} weight="fill" />
              ) : (
                <Warning size={20} weight="fill" />
              )}
              <span className="text-small">{mensaje.texto}</span>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {/* Datos de la empresa */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🏢 Datos de la empresa</h3>
            <div className="text-tiny text-secondary mb-3">
              Aparecen en facturas, presupuestos, la portada y la tarjeta digital. El teléfono es también el número de WhatsApp que ven los clientes.
            </div>
            <Input
              label="Nombre de la empresa"
              placeholder="AAGG Reformas"
              value={empresaDatos.nombre}
              onChange={(e) => setEmpresaDatos((p) => ({ ...p, nombre: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="CIF"
              placeholder="B12345678"
              value={empresaDatos.cif}
              onChange={(e) => setEmpresaDatos((p) => ({ ...p, cif: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Teléfono / WhatsApp"
              placeholder="600 000 000"
              value={empresaDatos.telefono}
              onChange={(e) => setEmpresaDatos((p) => ({ ...p, telefono: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Email de contacto"
              placeholder="reformasaagg@gmail.com"
              value={empresaDatos.email}
              onChange={(e) => setEmpresaDatos((p) => ({ ...p, email: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Dirección"
              placeholder="Lleida"
              value={empresaDatos.direccion}
              onChange={(e) => setEmpresaDatos((p) => ({ ...p, direccion: e.target.value }))}
              className="mb-4"
            />
            <Button variant="accent" onClick={guardarDatosEmpresa} disabled={guardandoDatos} icon={<FloppyDisk size={18} />}>
              {guardandoDatos ? "Guardando..." : "Guardar datos de la empresa"}
            </Button>
          </div>

          {/* Tarifa de mano de obra extra */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🧰 Tarifa de mano de obra extra</h3>
            <div className="text-tiny text-secondary mb-3">
              Se usa para calcular el recargo por "Condiciones de acceso" (pisos sin ascensor,
              calle estrecha, sin aparcamiento): cada condición suma unas horas fijas de trabajo
              extra, y esas horas se multiplican por esta tarifa para dar el recargo en euros —
              no un porcentaje del presupuesto total.
            </div>
            <Input
              label="€ por hora de mano de obra extra"
              placeholder="18"
              type="number"
              value={empresaTarifa.tarifa_hora_peon}
              onChange={(e) => setEmpresaTarifa({ tarifa_hora_peon: parseFloat(e.target.value) || 0 })}
              className="mb-4"
            />
            <Button variant="accent" onClick={guardarDatosTarifa} disabled={guardandoTarifa} icon={<FloppyDisk size={18} />}>
              {guardandoTarifa ? "Guardando..." : "Guardar tarifa"}
            </Button>
          </div>

          {/* Marca y presentación */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🎨 Marca y presentación</h3>
            <div className="text-tiny text-secondary mb-3">
              Logo, eslogan y disponibilidad que ven los clientes en la portada y la tarjeta digital. La garantía aparece en cada presupuesto en PDF.
            </div>

            <label className="input-label">Logo</label>
            <div className="flex items-center gap-3 mb-4">
              {empresaMarca.logo_url && (
                <img src={empresaMarca.logo_url} alt="Logo actual" style={{ height: 48, borderRadius: 8, background: "var(--surface-secondary)" }} />
              )}
              <input type="file" accept="image/*" onChange={handleLogoChange} className="input" />
            </div>

            <Input
              label="URL de la app (para el QR de la tarjeta digital)"
              placeholder="https://reformas-aagg.com"
              value={empresaMarca.web}
              onChange={(e) => setEmpresaMarca((p) => ({ ...p, web: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Eslogan"
              placeholder="Tu reforma, hecha bien. Sin sorpresas."
              value={empresaMarca.slogan}
              onChange={(e) => setEmpresaMarca((p) => ({ ...p, slogan: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Horario / disponibilidad"
              placeholder="Sábados y Domingos"
              value={empresaMarca.horario}
              onChange={(e) => setEmpresaMarca((p) => ({ ...p, horario: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Garantía en mano de obra (aparece en cada presupuesto)"
              placeholder="Garantía de 2 años en mano de obra"
              value={empresaMarca.garantia_texto}
              onChange={(e) => setEmpresaMarca((p) => ({ ...p, garantia_texto: e.target.value }))}
              className="mb-4"
            />
            <Button variant="accent" onClick={guardarDatosMarca} disabled={guardandoMarca} icon={<FloppyDisk size={18} />}>
              {guardandoMarca ? "Guardando..." : "Guardar marca y presentación"}
            </Button>
          </div>

          {/* Datos de cobro */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">💳 Datos de cobro</h3>
            <div className="text-tiny text-secondary mb-3">
              Aparecen al pie de cada presupuesto en PDF, para que el cliente sepa dónde pagarte.
            </div>
            <Input
              label="Titular de la cuenta"
              placeholder="Andy Gonzaga Castro"
              value={empresaPago.titular_cuenta}
              onChange={(e) => setEmpresaPago((p) => ({ ...p, titular_cuenta: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="IBAN (transferencia)"
              placeholder="ES00 0000 0000 0000 0000 0000"
              value={empresaPago.iban}
              onChange={(e) => setEmpresaPago((p) => ({ ...p, iban: e.target.value }))}
              className="mb-4"
            />
            <Input
              label="Bizum (teléfono o alias)"
              placeholder="600 000 000"
              value={empresaPago.bizum}
              onChange={(e) => setEmpresaPago((p) => ({ ...p, bizum: e.target.value }))}
              className="mb-4"
            />
            <Button variant="accent" onClick={guardarDatosPago} disabled={guardandoPago} icon={<FloppyDisk size={18} />}>
              {guardandoPago ? "Guardando..." : "Guardar datos de cobro"}
            </Button>
          </div>

          {/* URLs y Dominio */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🌐 URLs y Dominio</h3>

            <div className="card p-3 mb-4" style={{ background: "var(--warning-bg)", border: "1px solid var(--warning)" }}>
              <div className="flex items-start gap-2">
                <Warning size={20} weight="fill" style={{ color: "var(--warning)", flexShrink: 0 }} />
                <div className="text-tiny">
                  <strong>Backend URL y Dominio:</strong> Estos valores están configurados en las variables de entorno. 
                  Cambiarlos aquí NO afectará el funcionamiento real de la app. 
                  Para cambios reales, contacta con soporte técnico.
                </div>
              </div>
            </div>
            <Input
              label="Backend URL"
              placeholder="https://api.ejemplo.com"
              value={config?.backend_url || ""}
              onChange={(e) => setConfig(prev => ({ ...prev, backend_url: e.target.value }))}
              disabled
            />
            <Input
              label="Dominio"
              placeholder="ejemplo.com"
              value={config?.dominio || ""}
              onChange={(e) => setConfig(prev => ({ ...prev, dominio: e.target.value }))}
              disabled
            />
          </div>

          {/* API Keys */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">🔑 API Keys</h3>
            <div className="text-tiny text-secondary mt-2">
              ℹ️ La clave de OpenAI (OPENAI_API_KEY) y el modelo (OPENAI_MODEL) se configuran como variables de entorno en Railway, no aquí. Para cambiarlas, ve al servicio backend en Railway → Variables.
            </div>
          </div>

          {/* Funcionalidades */}
          <div className="card p-4">
            <h3 className="text-h3 font-display mb-4">⚡ Funcionalidades</h3>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={config?.pdf_generation_enabled || false}
                  onChange={(e) => setConfig(prev => ({ ...prev, pdf_generation_enabled: e.target.checked }))}
                  className="w-5 h-5"
                />
                <div>
                  <div className="text-small font-bold">Generación de PDF</div>
                  <div className="text-tiny text-secondary">Activa la generación de presupuestos en PDF</div>
                </div>
              </label>
            </div>
            <Button variant="accent" className="mt-4" onClick={guardarConfiguracion} disabled={guardando} icon={<FloppyDisk size={18} />}>
              {guardando ? "Guardando..." : "Guardar funcionalidades"}
            </Button>
          </div>

          {/* Información del sistema */}
          <div className="card p-4" style={{ background: "var(--surface-secondary)" }}>
            <h3 className="text-h3 font-display mb-3">ℹ️ Información del Sistema</h3>
            <div className="space-y-2 text-tiny">
              <div className="flex justify-between">
                <span className="text-secondary">Última actualización:</span>
                <span>{config?.updated_at ? new Date(config.updated_at).toLocaleString('es-ES') : "N/A"}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-secondary">ID Configuración:</span>
                <span className="font-mono">{config?.id || "N/A"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
